import {
  SnapshotDto,
  SnapshotFileNameDto,
  useGetSnapshotsQuery,
  useLazyGetSnapshotFileQuery,
  useRestoreSnapshotMutation,
} from "@store/services/snapshotService"
import {
  DecodedSnapshot,
  DecodedSnapshotMessage,
  decodeSnapshot,
} from "@features/builder/Snapshot/snapshotUitls"
import ProgressDialog, { ProgressDialogProps } from "@components/ProgressDialog"
import { FunctionComponent, useCallback, useEffect, useRef, useState } from "react"
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material"
import TableContainer from "@mui/material/TableContainer"
import Table from "@mui/material/Table"
import { format } from "date-fns"
import TableBody from "@mui/material/TableBody"
import { convertMessageToReadableString } from "@features/builder/Snapshot/snapshotLogUtils"
import { StructStack } from "@features/builder/crdt/crdtStructs"
import { enqueueSnackbar } from "notistack"
import { useDispatch } from "react-redux"
import { baseGraphFamiliesApi } from "@store/services/baseGraphFamiliesService"
import { useAtomValue } from "jotai"
import { SnapshotFilter, snapshotLensAtom } from "@features/builder/Snapshot/SnapshotFilter"
import { useIntersectionObserver } from "@hooks/useIntersectionObserver"

export const SnapshotRestoreDialog = (
  props: Pick<ProgressDialogProps, "onClose"> & {
    graphKey: string
  },
) => {
  const lens = useAtomValue(snapshotLensAtom)
  const { data: snapshotData, isFetching: isLoadingSnapshots } = useGetSnapshotsQuery(
    {
      key: props.graphKey,
      author_email: lens.email,
      to_date: lens.toDate,
      from_date: lens.fromDate,
      limit: lens.limit,
      page: lens.page,
    },
    { skip: !props.graphKey },
  )
  const [isRestoreSnapshotLoading, setIsRestoreSnapshotLoading] = useState(false)

  const ref = useRef<HTMLDivElement | null>()

  const handleNavigate = (snapshot: SnapshotDto) => {
    if (!ref.current) return
    Array.from(ref.current.children)
      .filter((s) => s.getAttribute("data-sha") == snapshot.previous_sha)[0]
      ?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const onSingleSnapshotSetRestoreSnapshotLoading = useCallback(
    (nextIsRestoreNapshotLoading: boolean) => {
      setIsRestoreSnapshotLoading(nextIsRestoreNapshotLoading)
    },
    [setIsRestoreSnapshotLoading],
  )

  const snapshotList = snapshotData?.map((snapshot: SnapshotDto) => (
    <SingleSnapshot
      snapshot={snapshot}
      graphKey={props.graphKey}
      key={snapshot.commit_sha}
      onNavigate={handleNavigate}
      onClose={props.onClose}
      onSetIsRestoreSnapshotLoading={onSingleSnapshotSetRestoreSnapshotLoading}
    />
  ))

  return (
    <ProgressDialog
      isLoading={isRestoreSnapshotLoading}
      open={true}
      onClose={props.onClose}
      title="Restore snapshot"
      fullScreen
      actionsComponent={
        <>
          <Button variant="contained" onClick={props.onClose}>
            Close
          </Button>
        </>
      }
    >
      <Grid container direction="column" gap={3}>
        <Grid item>
          <SnapshotFilter />
        </Grid>
        <Grid item>
          {isLoadingSnapshots && <CircularProgress />}
          {!isLoadingSnapshots && <Box ref={ref}>{snapshotList}</Box>}
        </Grid>
      </Grid>
    </ProgressDialog>
  )
}

const SingleSnapshot = ({
  snapshot,
  graphKey,
  onClose,
  onNavigate,
  onSetIsRestoreSnapshotLoading,
}: {
  snapshot: SnapshotDto
  graphKey: string
  onClose: () => void
  onNavigate: (snapshot: SnapshotDto) => void
  onSetIsRestoreSnapshotLoading: (nextIsRestoreNapshotLoading: boolean) => void
}) => {
  const [restoreSnapshot, { isLoading: isRestoreSnapshotLoading }] = useRestoreSnapshotMutation()
  const dispatch = useDispatch()

  const [fetchFile, { isFetching }] = useLazyGetSnapshotFileQuery()
  const [decodedSnapshot, setDecodedSnapshot] = useState<DecodedSnapshot | undefined>(undefined)
  const containerRef = useRef<HTMLDivElement>(null)
  const isRestoredSnapshot = snapshot.previous_sha !== ""

  const isVisible = useIntersectionObserver(containerRef, {
    rootMargin: "100px",
  })

  useEffect(() => {
    if (isRestoredSnapshot || !isVisible) return
    ;(async () => {
      const graphBin = await fetchFile({
        key: graphKey,
        fileName: SnapshotFileNameDto.GRAPH_BIN,
        commit_sha: snapshot.commit_sha,
      }).unwrap()
      const messagesBin = await fetchFile({
        key: graphKey,
        fileName: SnapshotFileNameDto.DELTA_MESSAGES_BIN,
        commit_sha: snapshot.commit_sha,
      }).unwrap()
      setDecodedSnapshot(decodeSnapshot(graphBin, messagesBin))
    })()
  }, [snapshot, graphKey, fetchFile, isRestoredSnapshot, isVisible])

  useEffect(() => {
    onSetIsRestoreSnapshotLoading(isRestoreSnapshotLoading)
  }, [onSetIsRestoreSnapshotLoading, isRestoreSnapshotLoading])

  const handleRestoreSnapshot = async () => {
    try {
      await restoreSnapshot({ key: graphKey, commit_sha: snapshot.commit_sha }).unwrap()
      onClose()
      dispatch(baseGraphFamiliesApi.util.invalidateTags(["BaseGraphFamilies"]))
    } catch (e) {
      enqueueSnackbar("Problem restoring snapshot", { variant: "error" })
    }
  }

  const handleNavigate = () => {
    onNavigate(snapshot)
  }

  return (
    <Box
      ref={containerRef}
      marginBottom={5}
      border={1}
      borderRadius={2}
      padding={2}
      data-sha={snapshot.commit_sha}
    >
      <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
        <p>
          Snapshot <b>{snapshot.commit_message}</b> version <b>{snapshot.version}</b> created by{" "}
          {snapshot.author_name} at {format(new Date(snapshot.date), "yyyy-MM-dd HH:mm:ss")}
        </p>
        {!isRestoredSnapshot && (
          <Button
            variant="contained"
            onClick={handleRestoreSnapshot}
            disabled={isRestoreSnapshotLoading}
          >
            Restore snapshot
          </Button>
        )}
        {isRestoredSnapshot && (
          <Button variant="contained" onClick={handleNavigate}>
            Navigate to snapshot
          </Button>
        )}
      </Box>
      {!isRestoredSnapshot && isFetching && <CircularProgress />}
      {!isRestoredSnapshot && !isFetching && isVisible && (
        <SnapshotTable messages={decodedSnapshot?.changes ?? []} stack={decodedSnapshot?.stack} />
      )}
    </Box>
  )
}

const SnapshotTable: FunctionComponent<{
  messages: DecodedSnapshotMessage[]
  stack?: StructStack
}> = (props) => {
  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell align="center">
              <strong>Action</strong>
            </TableCell>
            <TableCell>
              <strong>Message</strong>
            </TableCell>
            <TableCell>
              <strong>Author</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.messages.map((msg, i) => (
            <TableRow key={`row-${i}`}>
              <TableCell>
                {msg.messages
                  .map((m) => m.action)
                  .map((m) => (
                    // eslint-disable-next-line react/jsx-key -- fix it when you will have a chance to check this component
                    <span style={{ textTransform: "capitalize" }}>
                      {m}
                      <br />
                    </span>
                  ))}
              </TableCell>
              <TableCell style={{ maxWidth: 300 }}>
                {msg.messages
                  .map((m) => convertMessageToReadableString(m, props.stack))
                  .map((m) => (
                    // eslint-disable-next-line react/jsx-key -- fix it when you will have a chance to check this component
                    <span>
                      {m}
                      <br />
                    </span>
                  ))}
              </TableCell>
              <TableCell>{msg.author}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
